<template>
  <header class="game-header">
    <!-- Desktop Header -->
    <div class="stats-container desktop-header">
      <div class="player-info">
        <div class="level-badge">
          <div class="level-inner">
            <span class="level">{{ player?.level || 1 }}</span>
            <i class="fas fa-crown"></i>
          </div>
        </div>
      
        <div class="exp-container">
          <div class="exp-bar-wrapper">
            <div class="exp-bar" :style="{ width: experiencePercentage + '%' }">
              <div class="exp-particles"></div>
            </div>
          </div>
          <span class="exp-text">
            <i class="fas fa-star"></i>
            {{ player?.experience || 0 }} / {{ player?.maxExperience || 1000 }}
          </span>
        </div>
      </div>

      <div class="currency-container">
        <div class="currency coins">
          <i class="fas fa-coins"></i>
          <span>{{ formatNumber(player?.coins) }}</span>
        </div>
        <div class="currency paralleliums" @click="openShop">
          <i class="material-icons">toll</i>
          <span>{{ formatNumber(player?.paralleliums) }}</span>
          <button class="add-paralleliums-btn" title="Comprar Paralleliums">
            <i class="fas fa-plus"></i>
          </button>
        </div>
        <div class="header-buttons">
          <!-- Notification Box first -->
          <notification-box />
          <!-- Settings Button second -->
          <button class="settings-btn" @click="openSettings">
            <div class="neo-icon">
              <i class="fas fa-cog"></i>
              <div class="glow"></div>
            </div>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Mobile Header - Improved Professional Design -->
    <div class="stats-container mobile-header">
      <div class="mobile-grid">
        <!-- Level Badge -->
        <div class="mobile-level">
          <span>{{ player?.level || 1 }}</span>
          <small>LVL</small>
        </div>
        
        <!-- Experience Bar -->
        <div class="mobile-exp">
          <div class="exp-progress">
            <div class="exp-bar-wrapper">
              <div class="exp-bar" :style="{ width: experiencePercentage + '%' }">
                <div class="exp-particles"></div>
              </div>
            </div>
            <div class="exp-values">
              <span>EXP: {{ formatCompactNumber(player?.experience || 0) }}/{{ formatCompactNumber(player?.maxExperience || 1000) }}</span>
            </div>
          </div>
        </div>
        
        <!-- Currencies -->
        <div class="mobile-currencies">
          <div class="currency coins">
            <i class="fas fa-coins"></i>
            <span>{{ formatCompactNumber(player?.coins) }}</span>
          </div>
          <div class="currency paralleliums" @click="openShop">
            <i class="material-icons">toll</i>
            <span>{{ formatCompactNumber(player?.paralleliums) }}</span>
            <button class="add-paralleliums-btn" title="Comprar Paralleliums">
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
        
        <!-- Action Buttons -->
        <div class="mobile-actions">
          <notification-box />
          <button class="settings-btn" @click="openSettings">
            <div class="neo-icon">
              <i class="fas fa-cog"></i>
              <div class="glow"></div>
            </div>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Import ModalConfigs component -->
    <modal-configs />
  </header>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import NotificationBox from './NotificationBox.vue'
import ModalConfigs from '@/shared/components/ModalConfigs.vue'

export default {
  name: 'HeaderComponent',
  components: {
    NotificationBox,
    ModalConfigs
  },
  computed: {
    ...mapGetters('player', ['player']),
    experiencePercentage() {
      if (!this.player) return 0
      return (this.player.experience / this.player.maxExperience) * 100
    }
  },
  methods: {
    ...mapActions('Configs', ['toggleSettingsModal']),
    ...mapActions('SoundController', ['playMusic', 'playSound', 'initializeAudio']),
    formatNumber(num) {
      if (!num) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },
    formatCompactNumber(num) {
      if (!num) return '0'
      
      // Format large numbers in a compact way for mobile
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      } else {
        return num.toString()
      }
    },
    openSettings() {
      this.toggleSettingsModal(true)
      // Play UI sound when opening settings
      this.playSound({ category: 'ui', type: 'click' })
    },
    openShop() {
      // Emit event to App.vue to open shop modal
      this.$emit('open-shop');
      // Play UI sound when opening shop
      this.playSound({ category: 'ui', type: 'click' })
    }
  },
  mounted() {
    // Initialize audio when component mounts
    this.initializeAudio()
    
    // Play a random ambient music track when component mounts
    this.playMusic({ 
      category: 'ambient', 
      index: null,  // null for random track
      startPaused: false
    })
  }
}
</script>

<style lang="scss" scoped>
.game-header {
  background: linear-gradient(180deg, rgba(20,20,20,1) 0%, rgba(30,30,30,1) 100%);
  padding: 0.8rem;
  box-shadow: 0 2px 15px rgba(0,0,0,0.8);
  border-bottom: 1px solid rgba(255,255,255,0.1);
  position: fixed;
  top: 0;
  right: 0;
  z-index: 10;
  width: 100%;
}

.stats-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Professional Mobile Header with CSS Grid */
.mobile-header {
  display: none;
  width: 100%;
  padding: 0.4rem 0.5rem;
}

.mobile-grid {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto auto;
  grid-template-areas: 
    "level exp actions"
    "currency currency currency";
  gap: 0.5rem;
  width: 100%;
  height: 100%;
  align-items: center;
}

/* Level Badge in top-left, redesigned to be more compact */
.mobile-level {
  grid-area: level;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 2px solid #b38f00;
  border-radius: 6px;
  padding: 0.2rem 0.4rem;
  min-width: 2.5rem;
  height: 2.4rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.5), inset 0 1px rgba(255,255,255,0.1);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 35%;
    background: linear-gradient(to bottom, rgba(255,255,255,0.1), transparent);
  }
  
  span {
    color: #ffd700;
    font-weight: bold;
    font-size: 1rem;
    line-height: 1;
    letter-spacing: 0.5px;
    text-shadow: 0 2px 3px rgba(0,0,0,0.7);
    margin-bottom: -0.1rem;
  }
  
  small {
    color: #b38f00;
    font-size: 0.55rem;
    font-weight: 500;
    text-transform: uppercase;
    opacity: 0.9;
    letter-spacing: 0.5px;
  }
}

/* Experience bar in top-center with clean design */
.mobile-exp {
  grid-area: exp;
  padding: 0 0.3rem;
  
  .exp-progress {
    width: 100%;
    position: relative;
  }
  
  .exp-bar-wrapper {
    background: rgba(30,30,30,0.8);
    height: 0.5rem;
    border-radius: 3px;
    overflow: hidden;
    border: 1px solid rgba(128,0,128,0.3);
    box-shadow: inset 0 1px 3px rgba(0,0,0,0.5);
  }
  
  .exp-bar {
    height: 100%;
    background: linear-gradient(90deg, #4a0080, #9b00ff);
    position: relative;
    transition: width 0.3s ease;
    
    .exp-particles {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 30px;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
      animation: moveParticles 1.5s linear infinite;
    }
  }
  
  .exp-values {
    display: flex;
    justify-content: center;
    margin-top: 0.2rem;
    
    span {
      font-size: 0.65rem;
      color: #c0c0c0;
      font-weight: 500;
      background: rgba(0,0,0,0.2);
      padding: 0.1rem 0.3rem;
      border-radius: 2px;
      border: 1px solid rgba(128,0,128,0.2);
    }
  }
}

/* Action buttons in top-right with better touch targets */
.mobile-actions {
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

/* Currencies in bottom row - full width for better visibility */
.mobile-currencies {
  grid-area: currency;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  width: 100%;
}

@media (max-width: 768px) {
  .desktop-header {
    display: none;
  }
  
  .mobile-header {
    display: block;
  }
  
  .game-header {
    padding: 0.4rem 0.25rem;
  }
  
  /* Currency styling with consistent design */
  .currency {
    height: 2.1rem;
    padding: 0 0.6rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(10,10,10,0.8);
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.4), inset 0 1px rgba(255,255,255,0.05);
    
    i {
      font-size: 0.9rem;
    }
    
    span {
      font-size: 0.85rem;
      font-weight: 600;
      flex: 1;
      text-align: right;
      margin-left: 0.3rem;
    }
  }
  
  .coins {
    border: 1px solid rgba(255,215,0,0.4);
    
    .fas.fa-coins {
      color: #ffd700;
      text-shadow: 0 0 5px rgba(255,215,0,0.7);
    }
    
    span {
      color: #ffe147;
      text-shadow: 0 1px 3px rgba(0,0,0,0.8);
    }
  }
  
  .paralleliums {
    border: 1px solid rgba(0,255,255,0.4);
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(0,255,255,0.1);
      border-color: rgba(0,255,255,0.6);
      
      .add-paralleliums-btn {
        opacity: 1;
      }
    }
    
    .material-icons {
      color: #00ffff;
      text-shadow: 0 0 5px rgba(0,255,255,0.7);
      font-size: 0.9rem;
    }
    
    span {
      color: #47ffff;
      text-shadow: 0 1px 3px rgba(0,0,0,0.8);
    }
    
    .add-paralleliums-btn {
      position: absolute;
      right: -8px;
      top: -8px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #00ffff, #00cccc);
      border: 2px solid #0a0a0a;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0.8;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0,255,255,0.5);
      
      &:hover {
        transform: scale(1.1);
        box-shadow: 0 3px 12px rgba(0,255,255,0.8);
      }
      
      i {
        font-size: 10px;
        color: #0a0a0a;
      }
    }
  }
  
  /* Button styling for better touch targets */
  .settings-btn {
    padding: 0.15rem;
    
    .neo-icon {
      width: 2.1rem;
      height: 2.1rem;
      border-radius: 4px;
      
      i {
        font-size: 1rem;
      }
    }
  }
}

/* Medium-small screens optimization */
@media (max-width: 480px) {
  .mobile-grid {
    gap: 0.4rem;
  }
  
  .mobile-currencies {
    gap: 0.4rem;
  }
}

/* Very small screens optimization */
@media (max-width: 380px) {
  .mobile-grid {
    grid-template-columns: auto 1fr auto;
    grid-template-areas: 
      "level exp actions"
      "currency currency currency";
    gap: 0.3rem;
  }
  
  .mobile-level {
    min-width: 2.2rem;
    height: 2.2rem;
    padding: 0.1rem 0.3rem;
    
    span {
      font-size: 0.9rem;
    }
    
    small {
      font-size: 0.5rem;
    }
  }
  
  .mobile-exp .exp-values span {
    font-size: 0.6rem;
    padding: 0.05rem 0.2rem;
  }
  
  .currency {
    height: 1.9rem;
    padding: 0 0.4rem;
    
    i {
      font-size: 0.8rem;
    }
    
    span {
      font-size: 0.75rem;
    }
  }
  
  .mobile-currencies {
    gap: 0.3rem;
  }
  
  .settings-btn .neo-icon {
    width: 2rem;
    height: 2rem;
  }
}

.player-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.level-badge {
  position: relative;
  width: 3.5rem;
  height: 3.5rem;
  
  .level-inner {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, #2a2a2a, #1a1a1a);
    border-radius: 50%;
    border: 2px solid #b38f00;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 10px rgba(179,143,0,0.3);

    &::after {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: 50%;
      border: 1px solid rgba(255,215,0,0.3);
    }
  }

  .level {
    color: #ffd700;
    font-weight: bold;
    font-size: 1.2rem;
    text-shadow: 0 0 5px rgba(255,215,0,0.5);
  }

  .fa-crown {
    font-size: 0.7rem;
    color: #ffd700;
    margin-top: 2px;
    opacity: 0.7;
  }
}

.exp-container {
  flex: 1;
  max-width: 400px;
  
  .exp-bar-wrapper {
    background: rgba(0,0,0,0.5);
    height: 0.6rem;
    border-radius: 3px;
    overflow: hidden;
    border: 1px solid rgba(255,255,255,0.1);
  }

  .exp-bar {
    height: 100%;
    background: linear-gradient(90deg, #4a0080, #800080);
    position: relative;
    transition: width 0.3s ease;

    .exp-particles {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 50px;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
      animation: moveParticles 1.5s linear infinite;
    }
  }

  .exp-text {
    color: #a0a0a0;
    font-size: 0.75rem;
    margin-top: 0.3rem;
    display: flex;
    align-items: center;
    gap: 0.3rem;

    .fa-star {
      color: #800080;
      font-size: 0.7rem;
    }
  }
}

.currency-container {
  display: flex;
  gap: 0.8rem;
  align-items: center;

  .currency {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.8rem;
    background: rgba(0,0,0,0.3);
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    }

    i {
      font-size: 1.2rem;
    }

    span {
      color: #fff;
      font-weight: 500;
      font-size: 0.9rem;
      text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    }
  }

  .coins {
    border: 1px solid rgba(255,215,0,0.3);
    .fas.fa-coins { color: #ffd700; }
  }

  .paralleliums {
    border: 1px solid rgba(0,255,255,0.3);
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(0,255,255,0.15);
      border-color: rgba(0,255,255,0.5);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,255,255,0.3);
      
      .add-paralleliums-btn {
        opacity: 1;
      }
    }
    
    .material-icons { 
      color: #00ffff;
      text-shadow: 0 0 8px rgba(0,255,255,0.7);
    }
    
    .add-paralleliums-btn {
      position: absolute;
      right: -10px;
      top: -10px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: linear-gradient(135deg, #00ffff, #00cccc);
      border: 2px solid #0a0a0a;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0.9;
      transition: all 0.3s ease;
      box-shadow: 0 3px 10px rgba(0,255,255,0.6);
      
      &:hover {
        transform: scale(1.15);
        box-shadow: 0 4px 15px rgba(0,255,255,0.9);
      }
      
      i {
        font-size: 12px;
        color: #0a0a0a;
        font-weight: bold;
      }
    }
  }
}

.header-buttons {
  display: flex;
  align-items: center;
  gap: 0.2rem; /* Reduced gap to make buttons appear closer together */
}

.settings-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  position: relative;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  .neo-icon {
    position: relative;
    width: 2.4rem;
    height: 2.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    background: rgba(5, 5, 8, 1);
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    
    i {
      color: rgba(224, 224, 255, 1);
      font-size: 1.2rem;
      position: relative;
      z-index: 2;
      text-shadow: 0 0 5px rgba(196, 30, 58, 0.5);
    }
    
    .glow {
      position: absolute;
      width: 100%;
      height: 2px;
      background: #c41e3a;
      bottom: 0;
      left: 0;
      filter: blur(3px);
      opacity: 0.7;
      transform: translateY(50%);
      z-index: 1;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(196, 30, 58, 0.2), transparent);
    }
    
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(196, 30, 58, 0.6), transparent);
    }
  }
  
  &:hover .neo-icon {
    box-shadow: 0 0 20px rgba(196, 30, 58, 0.3);
    
    i {
      color: white;
      text-shadow: 0 0 8px rgba(196, 30, 58, 0.8);
    }
    
    .glow {
      opacity: 1;
      height: 3px;
      filter: blur(5px);
    }
  }
}

// Remove estes estilos pois foram substituídos
.icon-button {
  display: none;
}

.settings-button {
  display: none;
}

@keyframes moveParticles {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
</style>
